import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArchiveBackends } from "@graylog/enterprise-api";
import i18n from "i18n";
import fetch from "logic/rest/FetchProvider";
import { qualifyUrl } from "util/URLUtils";
import UserNotification from "util/UserNotification";
const urlPrefix = "/plugins/org.graylog.plugins.archive";
const url = (path) => qualifyUrl(`${urlPrefix}${path}`);
const errorMessageFor = (error) => {
  var _a, _b, _c, _d;
  return (_d = (_c = (_b = (_a = error.additional) == null ? void 0 : _a.body) == null ? void 0 : _b.message) != null ? _c : error.message) != null ? _d : i18n.t("notifications.fallback_error_message", { ns: "enterprise_archive" });
};
export const ARCHIVE_BACKENDS_QUERY_KEY = ["archive-backends"];
export const ARCHIVE_BACKEND_QUERY_KEY = ["archive-backend"];
const fetchBackends = (page, perPage) => ArchiveBackends.getConfigs(void 0, page, perPage).then((response) => ({
  backends: response.backends,
  pagination: {
    count: response.count,
    total: response.total,
    page: response.page,
    per_page: response.per_page,
    query: response.query
  }
})).catch((error) => {
  UserNotification.error(
    i18n.t("notifications.backends_fetch_failed", { ns: "enterprise_archive", error: errorMessageFor(error) }),
    i18n.t("notifications.backends_fetch_failed_title", { ns: "enterprise_archive" })
  );
  throw error;
});
const fetchBackend = (backendId) => ArchiveBackends.getConfig(backendId).catch((error) => {
  UserNotification.error(
    i18n.t("notifications.backend_fetch_failed", { ns: "enterprise_archive", error: errorMessageFor(error) }),
    i18n.t("notifications.backend_fetch_failed_title", { ns: "enterprise_archive" })
  );
  throw error;
});
export const saveArchiveBackend = (backend) => {
  const promise = backend.id ? fetch("PUT", url(`/backends/${backend.id}`), backend) : fetch("POST", url("/backends"), backend);
  return promise.then((response) => {
    UserNotification.success(i18n.t("notifications.backend_save_success", { ns: "enterprise_archive" }));
    return response;
  }).catch((error) => {
    UserNotification.error(
      i18n.t("notifications.backend_save_failed", { ns: "enterprise_archive", error: errorMessageFor(error) }),
      i18n.t("notifications.backend_save_failed_title", { ns: "enterprise_archive" })
    );
    throw error;
  });
};
export const deleteArchiveBackend = (backend, deleteArchives = false) => {
  if (!backend.id) {
    console.log("Tried to delete a backend without id, this should not happen.");
    return Promise.resolve();
  }
  return ArchiveBackends.remove(backend.id, deleteArchives).then(() => {
    UserNotification.success(
      i18n.t("notifications.backend_delete_success", { ns: "enterprise_archive", title: backend.title })
    );
  }).catch((error) => {
    UserNotification.error(
      i18n.t("notifications.backend_delete_failed", { ns: "enterprise_archive", error: errorMessageFor(error) }),
      i18n.t("notifications.backend_delete_failed_title", { ns: "enterprise_archive", title: backend.title })
    );
    throw error;
  });
};
export const useInvalidateArchiveBackends = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ARCHIVE_BACKENDS_QUERY_KEY });
};
export const useArchiveBackends = (page, perPage) => useQuery({
  queryKey: [...ARCHIVE_BACKENDS_QUERY_KEY, "paginated", page, perPage],
  queryFn: () => fetchBackends(page, perPage),
  placeholderData: keepPreviousData,
  retry: false
});
export const useArchiveBackend = (backendId) => useQuery({
  queryKey: [...ARCHIVE_BACKEND_QUERY_KEY, backendId],
  queryFn: () => fetchBackend(backendId),
  enabled: !!backendId,
  retry: false
});
